// infrastructure/mappers/coverageMapper.js
import { createCoverage } from '../../domain/model/Coverage';

/**
 * Anti-corruption layer between the `clientCoverages` documents and the Coverage aggregate.
 *
 * Four things this boundary is responsible for:
 *
 * 0. The document stores two value objects FLAT while the domain holds each as one: effective
 *    bounds as `effectiveStart` / `effectiveEnd` against `EffectivePeriod`, and the policyholder as
 *    `policyholderId` / `policyholderRelationship` against `Policyholder`. `policyholderId` is
 *    null when the client holds their own policy, which is the common case and not a missing value.
 *    Taking the bounds as the worked example: Flat is right for storage because these
 *    field names are the projector's contract: its `coverageResolver` reads them straight off the
 *    document. It does not FILTER on them — the query is by client and clinic, and the bounds are
 *    compared in memory — so the argument is not queryability, which a nested map would also
 *    support, but not breaking a reader already written against these names. Nested is right for
 *    the domain, where the bounds are one concept with rules of its own. This is the seam that
 *    lets each side have the shape it wants, the same way it already reconciles the domain's
 *    `clientId` with the document's `client`.
 * 1. Effective bounds are persisted as 'YYYY-MM-DD' strings, NOT Firestore Timestamps. An
 *    effective bound is a calendar date, and the billing projector compares it as one — its
 *    `toDateStr` accepts a string, a Date, or null, and a Timestamp is none of those, so a
 *    Timestamp would normalize to null and silently widen the coverage to open-ended.
 * 2. The persisted names match the projector's contract exactly (`fundingSourceId`,
 *    `effectiveStart`, `effectiveEnd`, `cobRank`) rather than inheriting the drift in the
 *    neighboring legacy collections, so the backend read side is a near-identity mapping.
 * 3. The scoping keys are the platform-standard bare `client` and `clinic`, because the Firestore
 *    rules helpers (`userHasAccessToClient`, `isSameRequiredClinicAfterUpdate`) read those exact
 *    field names — an `Id`-suffixed document could not use them. The domain keeps the clearer
 *    `clientId`/`clinicId`, and this boundary is where the two names meet, so the platform
 *    convention stays in one file instead of spreading through the context.
 *
 * `eligibilityVerification` is persisted as a MAP, not a boolean — verdict, source and the moment
 * it was established. A document written before that shape existed simply has no such field, and
 * reads back as unverified: conservative, since an unverified coverage is skipped in the billing
 * order rather than billed on. Nothing has shipped, so there is no migration to write; a stale
 * staging record just needs re-verifying.
 *
 * `isDeleted` IS persisted: removal is a soft delete, matching the platform pattern. Every reader
 * of this collection must therefore filter on it — a tombstone that slips through would be
 * selected as the client's payer and billed.
 */

/**
 * @param {Object} doc - a clientCoverages document
 * @returns {import('../../domain/model/Coverage.types').Coverage}
 */
export function toCoverage(doc) {
  return createCoverage({
    id: doc.id,
    clientId: doc.client ?? null,
    clinicId: doc.clinic ?? null,
    fundingSourceId: doc.fundingSourceId ?? null,
    policyholderId: doc.policyholderId ?? null,
    policyholderRelationship: doc.policyholderRelationship,
    eligibilityVerification: doc.eligibilityVerification,
    memberId: doc.memberId ?? null,
    groupNumber: doc.groupNumber ?? null,
    effectiveStart: doc.effectiveStart,
    effectiveEnd: doc.effectiveEnd,
    cobRank: doc.cobRank,
    notes: doc.notes ?? null,
    isDeleted: doc.isDeleted,
  });
} // toCoverage()

/**
 * @param {import('../../domain/model/Coverage.types').Coverage} coverage
 * @returns {Object} document fields, without the id
 */
export function toDocument(coverage) {
  return {
    client: coverage.clientId,
    clinic: coverage.clinicId,
    fundingSourceId: coverage.fundingSourceId,
    policyholderId: coverage.policyholder.personId,
    policyholderRelationship: coverage.policyholder.relationship,
    eligibilityVerification: coverage.eligibilityVerification,
    memberId: coverage.memberId,
    groupNumber: coverage.groupNumber,
    effectiveStart: coverage.period.start,
    effectiveEnd: coverage.period.end,
    cobRank: coverage.cobRank,
    notes: coverage.notes,
    isDeleted: coverage.isDeleted === true,
  };
} // toDocument()
